<template>
  <el-dialog
    v-model="dialogVisible"
    :title="$t('university.selectYourUniversity')"
    width="90%"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="true"
    center
    class="university-selection-dialog"
    :fullscreen="isMobile"
  >
    <div class="dialog-content">
      <!-- Search input -->
      <div class="search-container">
        <el-input
          v-model="searchQuery"
          :placeholder="$t('university.searchPlaceholder')"
          clearable
          size="large"
          class="search-input"
          @input="handleSearchInput"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        
        <!-- Search hints -->
        <transition name="fade">
          <div v-if="showSearchHints && searchQuery.trim()" class="search-hints">
            <div class="hint-item">
              <el-icon><InfoFilled /></el-icon>
              <span>{{ $t('university.searchHint') }}</span>
            </div>
          </div>
        </transition>
      </div>

      <!-- Selection hint -->
      <el-alert
        :title="$t('university.selectionHint')"
        type="info"
        :closable="false"
        show-icon
        class="hint-alert"
      />

      <!-- Loading state -->
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="6" animated />
      </div>

      <!-- Universities grid -->
      <div v-else-if="filteredUniversities.length > 0" class="universities-grid">
        <div
          v-for="university in filteredUniversities"
          :key="university.id"
          class="university-card"
          :class="{ selected: selectedUniversityId === university.id }"
          @click="selectUniversity(university.id)"
        >
          <div class="card-content">
            <div class="university-name">
              {{ university.name }}
              <span v-if="getAcronymForDisplay(university.name)" class="university-acronym">
                ({{ getAcronymForDisplay(university.name) }})
              </span>
            </div>
            <div class="university-location">
              <el-icon><Location /></el-icon>
              <span>{{ university.city }}, {{ university.state }}</span>
            </div>
            <div v-if="university.courseCount !== undefined" class="course-count">
              {{ $t('university.coursesAvailable', { count: university.courseCount }) }}
            </div>
          </div>
          <div v-if="selectedUniversityId === university.id" class="selected-indicator">
            <el-icon><Check /></el-icon>
          </div>
        </div>
      </div>

      <!-- No results -->
      <el-empty
        v-else
        :description="$t('university.noResults')"
        :image-size="120"
      />
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="skipSelection" size="large">
          {{ $t('common.skipForNow') }}
        </el-button>
        <el-button
          type="primary"
          @click="confirmSelection"
          :disabled="!selectedUniversityId"
          :loading="confirming"
          size="large"
        >
          {{ $t('common.confirm') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Location, Check } from '@element-plus/icons-vue'
import { useAuthStore } from '@/stores/auth'
import { universitiesApi } from '@/api/universities'
import { useI18n } from 'vue-i18n'
import type { University } from '@/types'
import { getUniversityAliases } from '@/data/universityAliases'
import { searchUniversities } from '@/utils/universitySearch'

// Props
interface Props {
  modelValue: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false
})

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'confirmed': [universityId: number]
  'skipped': []
}>()

// Composables
const { t } = useI18n()
const authStore = useAuthStore()

// Check if mobile
const isMobile = computed(() => window.innerWidth < 768)

// State
const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const universities = ref<University[]>([])
const loading = ref(false)
const confirming = ref(false)
const searchQuery = ref('')
const selectedUniversityId = ref<number | null>(null)
const showSearchHints = ref(false)
let searchHintTimeout: number | null = null

// Computed
const filteredUniversities = computed(() => {
  if (!searchQuery.value.trim()) {
    return universities.value
  }

  // 使用统一的智能搜索工具（支持别名和首字母联想）
  return searchUniversities(universities.value, searchQuery.value)
})

// Methods
const fetchUniversities = async () => {
  loading.value = true
  try {
    const response = await universitiesApi.getAll()
    universities.value = response.data || []
  } catch (error) {
    console.error('Failed to fetch universities:', error)
    ElMessage.error(t('university.fetchError'))
  } finally {
    loading.value = false
  }
}

const selectUniversity = (id: number) => {
  selectedUniversityId.value = id
}

const handleSearchInput = () => {
  // Show hints briefly when user starts typing
  if (searchHintTimeout) {
    clearTimeout(searchHintTimeout)
  }
  
  if (searchQuery.value.trim() && filteredUniversities.value.length > 0) {
    showSearchHints.value = true
    searchHintTimeout = setTimeout(() => {
      showSearchHints.value = false
    }, 3000)
  } else {
    showSearchHints.value = false
  }
}

const confirmSelection = async () => {
  if (!selectedUniversityId.value) {
    return
  }

  confirming.value = true
  try {
    const result = await authStore.selectUniversity(selectedUniversityId.value)
    if (result.success) {
      ElMessage.success(t('university.selectionSuccess'))
      emit('confirmed', selectedUniversityId.value)
      dialogVisible.value = false
    } else {
      ElMessage.error(t('university.selectionFailed'))
    }
  } catch (error) {
    console.error('Failed to confirm selection:', error)
    ElMessage.error(t('university.selectionFailed'))
  } finally {
    confirming.value = false
  }
}

const skipSelection = () => {
  emit('skipped')
  dialogVisible.value = false
}

// Helper: Get acronym for display (uses first alias from university_domains.json)
const getAcronymForDisplay = (name: string): string => {
  const aliases = getUniversityAliases(name)
  
  // Return the first alias if it exists and is short (likely an acronym)
  if (aliases.length > 0) {
    const firstAlias = aliases[0]
    // Only show if it's a short acronym (typically 2-5 characters)
    if (firstAlias.length >= 2 && firstAlias.length <= 10) {
      return firstAlias
    }
  }
  
  return ''
}

// Lifecycle
onMounted(() => {
  if (dialogVisible.value) {
    fetchUniversities()
  }
})

// Watch dialog visibility to fetch data
import { watch } from 'vue'
watch(dialogVisible, (newValue) => {
  if (newValue && universities.value.length === 0) {
    fetchUniversities()
  }
})
</script>

<style scoped lang="scss">
.university-selection-dialog {
  :deep(.el-dialog) {
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    max-height: 90vh;
  }

  :deep(.el-dialog__header) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 24px 30px;
    margin: 0;
    
    .el-dialog__title {
      color: white;
      font-size: 1.5rem;
      font-weight: 600;
    }

    .el-dialog__headerbtn {
      .el-dialog__close {
        color: white;
        font-size: 20px;
        
        &:hover {
          color: #f0f0f0;
        }
      }
    }
  }

  :deep(.el-dialog__body) {
    padding: 30px;
    background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);
  }

  :deep(.el-dialog__footer) {
    padding: 20px 30px;
    background: #f8f9fa;
    border-top: 1px solid #e4e7ed;
  }
}

.dialog-content {
  min-height: 400px;
  max-height: 60vh;
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #667eea;
    border-radius: 10px;
    
    &:hover {
      background: #764ba2;
    }
  }
}

.search-container {
  margin-bottom: 24px;
  position: relative;

  .search-input {
    width: 100%;
    
    :deep(.el-input__wrapper) {
      border-radius: 12px;
      padding: 12px 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
      }

      &.is-focus {
        box-shadow: 0 4px 16px rgba(102, 126, 234, 0.25);
      }
    }
  }
  
  .search-hints {
    margin-top: 12px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #e8f4ff 0%, #f0f8ff 100%);
    border-radius: 10px;
    border: 1px solid rgba(102, 126, 234, 0.2);
    animation: slideDown 0.3s ease;
    
    .hint-item {
      display: flex;
      align-items: center;
      gap: 10px;
      color: #667eea;
      font-size: 14px;
      font-weight: 500;
      
      .el-icon {
        font-size: 18px;
      }
    }
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.3s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

.hint-alert {
  margin-bottom: 28px;
  border-radius: 12px;
  animation: fadeInDown 0.5s ease;

  :deep(.el-alert__content) {
    padding: 8px 0;
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.loading-container {
  padding: 20px 0;
}

.universities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  padding: 8px 0;
  animation: fadeIn 0.6s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.university-card {
  position: relative;
  border: 2px solid #e4e7ed;
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    border-color: #667eea;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
    transform: translateY(-4px);

    &::before {
      transform: scaleX(1);
    }

    .university-name {
      color: #667eea;
    }
  }

  &.selected {
    border-color: #667eea;
    background: linear-gradient(135deg, #e6f0ff 0%, #f0f5ff 100%);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
    transform: translateY(-2px);

    &::before {
      transform: scaleX(1);
    }

    .university-name {
      color: #667eea;
      font-weight: 700;
    }

    .university-location {
      color: #667eea;
    }

    animation: selectPulse 0.4s ease;
  }
}

@keyframes selectPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

.card-content {
  .university-name {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 14px;
    line-height: 1.5;
    min-height: 54px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    transition: color 0.3s ease;
    
    .university-acronym {
      font-size: 14px;
      font-weight: 500;
      color: #667eea;
      background: rgba(102, 126, 234, 0.1);
      padding: 4px 10px;
      border-radius: 6px;
      white-space: nowrap;
    }
  }

  .university-location {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #909399;
    font-size: 14px;
    margin-bottom: 8px;
    transition: color 0.3s ease;

    .el-icon {
      font-size: 18px;
    }
  }

  .course-count {
    color: #67c23a;
    font-size: 13px;
    font-weight: 500;
    margin-top: 8px;
  }
}

.selected-indicator {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  animation: checkmarkBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes checkmarkBounce {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }
  50% {
    transform: scale(1.2) rotate(10deg);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;

  .el-button {
    padding: 12px 28px;
    border-radius: 10px;
    font-weight: 500;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .universities-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .university-selection-dialog {
    :deep(.el-dialog) {
      width: 100% !important;
      margin: 0 !important;
      border-radius: 0;
    }

    :deep(.el-dialog__body) {
      padding: 20px;
    }
  }

  .university-card {
    padding: 20px;

    .card-content .university-name {
      min-height: auto;
      font-size: 16px;
    }
  }

  .dialog-footer {
    flex-direction: column;
    gap: 12px;

    .el-button {
      width: 100%;
    }
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .universities-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .university-selection-dialog {
    :deep(.el-dialog) {
      width: 85% !important;
    }
  }
}

@media (min-width: 1025px) {
  .university-selection-dialog {
    :deep(.el-dialog) {
      max-width: 1200px;
    }
  }
}
</style>
